import {
  Container,
  LoaderWrapper,
  NoContentLabel,
  ProductsTitle,
  ProductsTitleWrapper,
  ProductsWrapper,
  PricingLabel,
  PricingRow,
  PricingRowTotal,
  PricingValue,
  PricingValueTotal,
  PricingWrapper,
  AdditionalCartDetailsWrapper,
  AdditionalCartDetailsTitle,
  AdditionalCartDetailsContent,
  BottomWrapper,
  SupplierGroupWrapper,
  SupplierGroupTitle,
} from './OverviewTab.styled';
import { useIsQuotationMode } from 'utils/hooks/useIsQuotationMode';
import { useActiveCartId } from 'utils/hooks/useActiveCartId';
import ShoppingCartCard from 'pages/ShoppingCartPage/ShoppingCartCard/ShoppingCartCard';
import { ICartItem } from 'types/CartItem.types';
import { useTranslation } from 'react-i18next';
import { useGetShoppingCart } from 'pages/ShoppingCartPage/hooks';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { useState, useEffect } from 'react';
import Button from 'components/Button/Button';
import { setActiveTab, setOrderProductsStep } from 'store/Order/actions/order';
import { OrderProductsSteps, OrderTabsEnum } from 'types/Order.types';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import Loader from 'components/Loader/Loader';
import { useDeleteItemFromShoppingCart } from 'pages/ShoppingCartPage/ShoppingCartCard/hooks';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import { AppMode } from 'store/Common/reducers/common';
import {
  setAdditionalCartDetails,
  setMaxConnLeadTime,
} from 'store/ShoppingCart/actions/shoppingCart';

const OverviewTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isRemoveItemDangerModalOpen, setIsRemoveItemDangerModalOpen] =
    useState<boolean>(false);
  const [cartItemIdForRemoval, setCartItemIdForRemoval] = useState<
    number | null
  >(null);

  const maxConnLeadTime = useSelector(
    (state: IRootReducerState) => state.shoppingCartInfo.maxConnLeadTime
  );
  const shoppingCartId = useActiveCartId();
  const companyId = useSelector(
    (state: IRootReducerState) => state.orderInfo.companyId
  );
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const isQuotationMode = useIsQuotationMode();
  const { data: cartData, isLoading: isLoadingShoppingCart } =
    useGetShoppingCart(appMode, shoppingCartId);

  const formatMoney = useFormatMoney(cartData?.currency);

  // Orders return `lines` as an array; quotations return it grouped by supplier
  // (Record<supplierName, ICartItem[]>). Right after switching flows, cartData
  // can briefly be the *other* flow's cart (shared shoppingCartId + react-query
  // cache) before the reset/re-fetch settles, so guard on the actual shape —
  // not just isQuotationMode — to avoid running the wrong branch on stale data.
  const linesAreGrouped = !!cartData?.lines && !Array.isArray(cartData.lines);

  const orderLines: ICartItem[] =
    !isQuotationMode && Array.isArray(cartData?.lines) ? cartData.lines : [];
  const quotationLineGroups: [string, ICartItem[]][] =
    isQuotationMode && linesAreGrouped
      ? Object.entries(cartData.lines as Record<string, ICartItem[]>)
      : [];
  const hasLines = isQuotationMode
    ? quotationLineGroups.some(([, items]) => items.length > 0)
    : orderLines.length > 0;

  const renderCartCard = (item: ICartItem) => (
    <ShoppingCartCard
      item={item}
      key={`cart-item-${item.id}`}
      onRemove={() => {
        setIsRemoveItemDangerModalOpen(true);
        setCartItemIdForRemoval(item.id);
      }}
      isPreview={false}
      buyerMode={appMode === AppMode.BUYER ? true : false}
      isGrossVisible={cartData.is_gross_visible}
      currency={cartData?.currency}
    />
  );

  // Dispatch maxConnLeadTime to Redux when cartData is available
  useEffect(() => {
    if (
      cartData?.max_conn_lead_time !== undefined &&
      cartData?.max_conn_lead_time !== null &&
      cartData.max_conn_lead_time !== maxConnLeadTime
    ) {
      dispatch(setMaxConnLeadTime(cartData.max_conn_lead_time));
    }

    if (cartData?.additional_cart_details?.length > 0) {
      dispatch(setAdditionalCartDetails(cartData.additional_cart_details));
    }
  }, [cartData, maxConnLeadTime, dispatch]);

  const { mutate: deleteItemFromShoppingCart } =
    useDeleteItemFromShoppingCart();
  return (
    <Container>
      <ProductsWrapper>
        <ProductsTitleWrapper>
          <ProductsTitle>{t('Products')}</ProductsTitle>
          <Button
            label={t('Add products')}
            secondary
            onClick={() => {
              dispatch(setOrderProductsStep(OrderProductsSteps.Selection));
              if (isQuotationMode && !companyId) {
                dispatch(setActiveTab(OrderTabsEnum.Company));
              } else {
                dispatch(setActiveTab(OrderTabsEnum.Products));
              }
            }}
          />
        </ProductsTitleWrapper>

        {isLoadingShoppingCart && (
          <LoaderWrapper>
            <Loader position="relative" />
          </LoaderWrapper>
        )}

        {isQuotationMode
          ? quotationLineGroups.map(([supplierName, items], groupIndex) => (
              <SupplierGroupWrapper
                key={`supplier-group-${supplierName}`}
                $isOdd={groupIndex % 2 === 1}
              >
                <SupplierGroupTitle>{supplierName}</SupplierGroupTitle>
                {items?.map((item: ICartItem) => renderCartCard(item))}
              </SupplierGroupWrapper>
            ))
          : orderLines?.map((item: ICartItem) => renderCartCard(item))}

        {!hasLines && !isLoadingShoppingCart && (
          <NoContentLabel>
            {t('Shopping cart is currently empty')}
          </NoContentLabel>
        )}

        <BottomWrapper>
          {cartData?.additional_cart_details && (
            <AdditionalCartDetailsWrapper>
              <AdditionalCartDetailsTitle>
                {t('Additional cart details')}
              </AdditionalCartDetailsTitle>
              <AdditionalCartDetailsContent>
                {cartData.additional_cart_details}
              </AdditionalCartDetailsContent>
            </AdditionalCartDetailsWrapper>
          )}

          {hasLines && !isLoadingShoppingCart && (
            <PricingWrapper>
              {cartData?.total_gross_price !== cartData?.total_net_price &&
                cartData?.is_gross_visible && (
                  <PricingRow>
                    <PricingLabel>{t('Subtotal')}:</PricingLabel>
                    <PricingValue>
                      {formatMoney(cartData?.total_gross_price)}
                    </PricingValue>
                  </PricingRow>
                )}
              {cartData?.total_gross_price !== cartData?.total_net_price &&
                cartData?.is_gross_visible && (
                  <PricingRow>
                    <PricingLabel>{t('Discount')}:</PricingLabel>
                    <PricingValue>
                      {formatMoney(cartData?.total_model_discount)}
                    </PricingValue>
                  </PricingRow>
                )}
              <PricingRow>
                <PricingLabel>{t('Total excl. VAT')}:</PricingLabel>
                <PricingValue>
                  {formatMoney(cartData?.total_net_price)}
                </PricingValue>
              </PricingRow>
              <PricingRow>
                <PricingLabel>
                  {cartData?.vat_percentage
                    ? `${t('VAT')} (${cartData.vat_percentage}%)`
                    : t('VAT')}
                  :
                </PricingLabel>
                <PricingValue>{formatMoney(cartData?.vat)}</PricingValue>
              </PricingRow>
              <PricingRowTotal>
                <PricingLabel>{t('Total incl. VAT')}:</PricingLabel>
                <PricingValueTotal>
                  {formatMoney(cartData?.total_net_price_incl_vat)}
                </PricingValueTotal>
              </PricingRowTotal>
            </PricingWrapper>
          )}
        </BottomWrapper>
      </ProductsWrapper>

      <DangerModal
        isOpen={isRemoveItemDangerModalOpen}
        setIsOpen={setIsRemoveItemDangerModalOpen}
        title={t('Confirm removal')}
        submit={{
          onClick: () => {
            if (cartItemIdForRemoval !== null) {
              deleteItemFromShoppingCart({
                itemId: cartItemIdForRemoval,
                shoppingCartId,
              });
              setIsRemoveItemDangerModalOpen(false);
            } else {
              alert('cartItemIdForRemoval is null');
            }
          },
          text: t('Remove'),
          disabled: false,
        }}
        cancel={{
          onClick: () => {
            setCartItemIdForRemoval(null);
            setIsRemoveItemDangerModalOpen(false);
          },
        }}
        description={
          t('Are you sure you want to remove this product from order') + '?'
        }
      />
    </Container>
  );
};

export default OverviewTab;
